import { JobEventTypeEnum, JobId } from '@eigtech/restoration-types'
import { GetTimelineResponse } from '@eigtech/summarizer-types'
import { useQueryClient } from '@eigtech/ui-shared-api'
import { useAuth0 } from '@eigtech/ui-shared-auth'
import { secondsToMilliseconds } from '@eigtech/ui-shared-dates'
import { useCallback } from 'react'
import { getJobTimelineEventLabel } from '../utils/getJobTimelineLabel'
import { jobsQueryKeys } from './constants'
import { useInvalidateJob, useInvalidateJobs } from './invalidateJobs'

type AddJobTimelineEventOptions<TDetails> = {
  jobId: JobId
  timelineEvent: Exclude<JobEventTypeEnum, 'workDetailsReceived'>
  point: {
    date?: string
    details?: TDetails
  }
}

export function useOptimisticAddJobTimelineEvent() {
  const invalidateJob = useInvalidateJob()
  const invalidateJobs = useInvalidateJobs()

  const { user } = useAuth0()
  const queryClient = useQueryClient()

  return useCallback(
    async <TDetails>({ jobId, timelineEvent, point }: AddJobTimelineEventOptions<TDetails>) => {
      const queryKey = jobsQueryKeys.timeline(jobId)

      await queryClient.cancelQueries({ queryKey })

      const previous = queryClient.getQueryData<GetTimelineResponse>(queryKey)

      if (previous) {
        queryClient.setQueryData<GetTimelineResponse>(queryKey, () => ({
          ...previous,
          timeline: {
            ...previous.timeline,
            chronicles: [
              ...(previous.timeline?.chronicles ?? []),
              {
                createdBy: user?.sub ?? 'User',
                date: new Date().toISOString(),
                domain: 'restoration',
                label: getJobTimelineEventLabel(timelineEvent),
                type: timelineEvent,
                ...point,
                details: {
                  recordedDate: new Date().toISOString(),
                  ...point.details,
                },
              },
            ],
          },
        }))
      }

      setTimeout(() => {
        invalidateJob(jobId)
        invalidateJobs()
      }, secondsToMilliseconds(10))
    },
    [invalidateJob, invalidateJobs, queryClient, user?.sub]
  )
}
